import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, TextField, IconButton } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import NumberLine from './NumberLine';
import NotationGuide from './NotationGuide';
import NumberLineControls from './NumberLineControls';
import useNumberLine from './useNumberLine';
import {
  defaultNumberLineConfig,
  formatElementSummary,
  getUnionAllowedModes,
  parseIntervalNotation,
  snapAndValidateBounds,
} from './numberLineUtils';

/**
 * Student UI for placing points/intervals/rays on the number line
 */
const NumberLineStudent = ({
  config = defaultNumberLineConfig,
  value = { elements: [] },
  onChange,
  question = null,
}) => {
  const mergedConfig = { ...defaultNumberLineConfig, ...config };

  // Get allowed modes - union of config modes and inferred from answer
  const allowedModes = question
    ? getUnionAllowedModes(question)
    : mergedConfig.allowedModes || ['point'];

  const {
    elements,
    mode,
    setMode,
    selectedId,
    setSelectedId,
    pendingValue,
    canUndo,
    addPoint,
    addInterval,
    addRay,
    removeElement,
    toggleEndpoint,
    undo,
    clear,
    handleLineClick,
    cancelOperation,
    setValue,
    getValue,
  } = useNumberLine(value, mergedConfig);

  // Notation text input state
  const [notationInput, setNotationInput] = useState('');
  const [notationError, setNotationError] = useState('');

  // Initialize with value
  useEffect(() => {
    if (value?.elements) {
      setValue(value);
    }
  }, []);

  // Sync elements to parent
  useEffect(() => {
    if (onChange) {
      onChange(getValue());
    }
  }, [elements]);

  // Set initial mode to first allowed mode
  useEffect(() => {
    if (allowedModes.length > 0 && !allowedModes.includes(mode)) {
      setMode(allowedModes[0]);
    }
  }, [allowedModes, mode, setMode]);

  // Handle notation text input submission
  const handleNotationSubmit = () => {
    const result = parseIntervalNotation(notationInput);
    if (!result.valid) {
      setNotationError(result.error);
      return;
    }

    const el = result.element;

    // Check element type is allowed
    if (!allowedModes.includes(el.type)) {
      setNotationError(`This question only allows: ${allowedModes.join(', ')}`);
      return;
    }

    // Snap and validate bounds
    const boundsError = snapAndValidateBounds(el, mergedConfig);
    if (boundsError) {
      setNotationError(boundsError);
      return;
    }

    // Add element via hook (preserve student-typed labels when still valid post-snap)
    if (el.type === 'point') {
      addPoint(el.value, el.label);
    } else if (el.type === 'interval') {
      addInterval(el.start, el.end, el.startOpen, el.endOpen, el.startLabel, el.endLabel);
    } else if (el.type === 'ray') {
      addRay(el.origin, el.direction, el.originOpen, el.originLabel);
    }

    setNotationInput('');
    setNotationError('');
  };

  const handleElementClick = (id, endpoint) => {
    if (endpoint) {
      // Clicked on endpoint - toggle open/closed
      toggleEndpoint(id, endpoint);
    } else if (selectedId === id) {
      // Double-click to delete
      removeElement(id);
    } else {
      setSelectedId(id);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Notation Guide */}
      <NotationGuide />

      {/* Instructions Box */}
      <Box sx={{ p: 1.5, backgroundColor: 'rgba(33, 150, 243, 0.08)', borderRadius: 1, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          How to use:
        </Typography>
        <Typography variant="body2" component="div" sx={{ '& ul': { m: 0, pl: 2.5 }, '& li': { mb: 0.5 } }}>
          <ul>
            <li>Click on the number line to place a point, or start an interval/ray, or type notation below (e.g., <code>[1, 5)</code>, <code>(-inf, 0]</code>, <code>3</code>)</li>
            <li>Click an element to select it (black border), click again to delete</li>
            <li>For intervals/rays, click on an endpoint to toggle:
              <ul>
                <li><strong>● Filled circle</strong> = Inclusive (includes the value, e.g., x ≤ 3)</li>
                <li><strong>○ Open circle</strong> = Exclusive (excludes the value, e.g., x &lt; 3)</li>
              </ul>
            </li>
          </ul>
        </Typography>
      </Box>

      {/* Mode Controls */}
      <NumberLineControls
        mode={mode}
        onModeChange={setMode}
        allowedModes={allowedModes}
        canUndo={canUndo}
        onUndo={undo}
        onClear={clear}
        pendingValue={pendingValue}
        onCancelOperation={cancelOperation}
        compact={true}
      />

      {/* Number Line */}
      <NumberLine
        config={mergedConfig}
        elements={elements}
        pendingValue={pendingValue}
        mode={mode}
        readOnly={false}
        onLineClick={handleLineClick}
        onElementClick={handleElementClick}
        selectedId={selectedId}
        width={600}
        height={100}
      />

      {/* Notation Text Input */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          placeholder="e.g., [1, 5), (-inf, 0], 3"
          value={notationInput}
          onChange={(e) => {
            setNotationInput(e.target.value);
            if (notationError) setNotationError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleNotationSubmit();
            }
          }}
          error={!!notationError}
          helperText={notationError}
          sx={{ flex: 1, maxWidth: 300 }}
        />
        <IconButton
          onClick={handleNotationSubmit}
          color="primary"
          disabled={!notationInput.trim()}
          sx={{ mt: 0.25 }}
        >
          <AddCircleOutlineIcon />
        </IconButton>
      </Box>

      {/* Current Elements Display */}
      {elements.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Your answer:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {elements.map((el, i) => (
              <Chip
                key={el.id || i}
                label={formatElementSummary(el)}
                size="small"
                onDelete={() => removeElement(el.id)}
                color={selectedId === el.id ? 'primary' : 'default'}
                onClick={() => setSelectedId(el.id)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NumberLineStudent;
